import React, { useMemo } from 'react';
import CalendarAndEvent from '../../events/components/CalendarAndEvent';
import { isSquadManager } from '../utils/squadRoles';

/**
 * ScheduleTab - Self-Contained Schedule/Calendar Management Component
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 *
 * Features:
 * - View events in calendar (list, weekly, monthly views)
 * - Create new events with date, time, location
 * - Edit existing events
 * - Delete events
 * - Respond to events (available, join-late, unavailable)
 * - View event responses from other members
 * - Google Maps integration for location autocomplete
 *
 * This component is a thin wrapper around CalendarAndEvent.
 * CalendarAndEvent is already self-contained and uses the useEvents hook internally.
 */

const ScheduleTab = ({ voProvider }) => {
  // Guard: Return loading state if voProvider not available
  if (!voProvider) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Get data from VO - memoize to prevent unnecessary re-renders
  const group = voProvider.getGroup();
  const groupId = useMemo(() => voProvider.vo.groupId, [voProvider.vo.groupId]);
  const isMember = useMemo(() => group?.isMember, [group?.isMember]);
  const isAdmin = useMemo(() => isSquadManager(group?.userRole), [group?.userRole]);

  /**
   * Handle event response update callback
   * CalendarAndEvent calls this when a user updates their event response
   * CalendarAndEvent already refreshes its own events via useEvents hook,
   * so we just need to log for debugging
   */
  const handleEventResponseUpdate = useMemo(() => {
    return () => {
      // CalendarAndEvent manages its own events via useEvents hook
      // No need to manually refresh here
    };
  }, []);

  return (
    <CalendarAndEvent
      ownerType="group"
      ownerId={groupId}
      isMember={isMember}
      isAdmin={isAdmin}
      onEventResponseUpdate={handleEventResponseUpdate}
      // Who a new event can invite, and whether it is a class (players and
      // coaches by default) rather than a normal squad (everybody).
      squadMembers={voProvider.getMembers()}
      isClassSquad={Boolean(group?.programId)}
    />
  );
};

export default ScheduleTab;
